'use client';

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Dices, Loader2, Search, X } from 'lucide-react';
import type { QuestCard as QuestCardData } from '@irl-sidequests/shared/quest-card';
import { QuestCard } from '@/components/quest-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { gtagEvent } from '@/lib/analytics';
import {
  BUDGET_OPTIONS, EMPTY_FILTERS, MOODS, TIME_OPTIONS, WHO_OPTIONS,
  finderParams, hasAnyFilter, interleaveByCategory, matchesFinder, parseFinderParams, rankForFinder,
  type FinderFilters,
} from '@/lib/finder';
import Link from 'next/link';

const CATEGORIES = ['Urban Exploration', 'Nature & Outdoors', 'Creative Arts', 'Personal Growth', 'Social & Community'];
const DIFFICULTIES = ['Beginner', 'Intermediate', 'Expert'];
const LOCATIONS = ['Indoor', 'Outdoor', 'Both'];
const PAGE = 24;

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Badge
      variant={active ? 'default' : 'outline'}
      className="cursor-pointer select-none py-1.5 px-3 text-sm font-normal"
      onClick={onClick}
      role="button"
      aria-pressed={active}
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    >
      {children}
    </Badge>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground w-16 shrink-0">{label}</span>
      {children}
    </div>
  );
}

function CatalogHeader({ total }: { total: number }) {
  return (
    <header className="mb-6">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">All {Math.floor(total / 100) * 100}+ side quests</h1>
      <p className="text-lg text-muted-foreground mt-2 max-w-2xl">
        Every quest on the site. Narrow it down by how much time you have, who&apos;s coming and what you want to spend, or just start scrolling.
      </p>
    </header>
  );
}

function CatalogGrid({ quests }: { quests: QuestCardData[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-test="catalog-grid">
      {quests.map((q, i) => <QuestCard key={q.slug} quest={q} surface="catalog" position={i} fetchpriority={i < 3 ? 'high' : 'low'} />)}
    </div>
  );
}

// What the server (and every crawler) gets. `useSearchParams` in a static page
// makes Next render the client tree only in the browser, so with a `null`
// fallback the crawlable HTML had no cards at all (smoke test 2026-10-13).
// Same header and first page of cards; the interactive catalog takes over
// after hydration without a layout shift.
function CatalogStatic({ initial, total }: { initial: QuestCardData[]; total: number }) {
  return (
    <div>
      <CatalogHeader total={total} />
      <p className="text-muted-foreground mb-6" data-test="catalog-count">
        <span className="font-semibold text-foreground">{total.toLocaleString()}</span> quests
      </p>
      <CatalogGrid quests={initial} />
    </div>
  );
}

function QuestsClientInner({ initial, total }: { initial: QuestCardData[]; total: number }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [filters, setFilters] = useState<FinderFilters>(() => parseFinderParams(searchParams));
  const [catalog, setCatalog] = useState<QuestCardData[] | null>(null);
  const [visible, setVisible] = useState(PAGE);
  const [search, setSearch] = useState(filters.search || '');
  const fetched = useRef(false);

  // The full light catalog loads once after paint; until then the server's first page stands in.
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetch('/api/quests-light')
      .then(r => (r.ok ? r.json() : []))
      .then((data: QuestCardData[]) => setCatalog(interleaveByCategory(rankForFinder(data, 0))))
      .catch(() => setCatalog([]));
  }, []);

  // Keep the URL in sync (shareable filters; canonical stays /quests).
  useEffect(() => {
    const p = finderParams(filters).toString();
    const current = searchParams.toString();
    if (p !== current) router.replace(p ? `${pathname}?${p}` : pathname, { scroll: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Debounced search → filters.
  useEffect(() => {
    const t = setTimeout(() => setFilters(f => (f.search === (search.trim() || undefined) ? f : { ...f, search: search.trim() || undefined })), 250);
    return () => clearTimeout(t);
  }, [search]);

  const source = catalog ?? initial;
  const filtered = useMemo(() => source.filter(c => matchesFinder(c, filters)), [source, filters]);
  const active = hasAnyFilter(filters);
  useEffect(() => { setVisible(PAGE); }, [filters]);
  useEffect(() => {
    if (active) gtagEvent('catalog_filter', { ...finderParamsObject(filters), result_count: filtered.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const set = useCallback(<K extends keyof FinderFilters>(k: K, v: FinderFilters[K]) => setFilters(f => ({ ...f, [k]: f[k] === v ? (k === 'time' || k === 'who' || k === 'budget' || k === 'mood' ? 'any' : undefined) : v })), []);
  const clear = () => { setFilters(EMPTY_FILTERS); setSearch(''); };
  const surprise = () => {
    const pool = filtered.length ? filtered : source;
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    gtagEvent('finder_surprise', { ...finderParamsObject(filters), result_slug: pick.slug, surface: 'catalog' });
    router.push(`/quest/${pick.slug}`);
  };

  const loadingFull = !catalog && active;

  return (
    <div>
      <CatalogHeader total={total} />

      <div className="rounded-2xl border bg-card p-4 sm:p-5 space-y-3 mb-6" data-test="catalog-filters">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search quests…"
            aria-label="Search quests"
            className="w-full rounded-lg border bg-background pl-9 pr-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <FilterGroup label="Mood">
          {MOODS.filter(m => m.value !== 'surprise').map(m => (
            <Chip key={m.value} active={filters.mood === m.value} onClick={() => set('mood', m.value as FinderFilters['mood'])}>
              <span aria-hidden="true" className="mr-1">{m.emoji}</span>{m.label}
            </Chip>
          ))}
        </FilterGroup>
        <FilterGroup label="Time">
          {TIME_OPTIONS.map(o => <Chip key={o.value} active={filters.time === o.value} onClick={() => set('time', o.value)}>{o.label}</Chip>)}
        </FilterGroup>
        <FilterGroup label="Who">
          {WHO_OPTIONS.map(o => <Chip key={o.value} active={filters.who === o.value} onClick={() => set('who', o.value)}>{o.label}</Chip>)}
        </FilterGroup>
        <FilterGroup label="Budget">
          {BUDGET_OPTIONS.filter(o => o.value !== 'any').map(o => <Chip key={o.value} active={filters.budget === o.value} onClick={() => set('budget', o.value)}>{o.label}</Chip>)}
        </FilterGroup>
        <FilterGroup label="Category">
          {CATEGORIES.map(c => <Chip key={c} active={filters.category === c} onClick={() => set('category', c)}>{c}</Chip>)}
        </FilterGroup>
        <FilterGroup label="Level">
          {DIFFICULTIES.map(d => <Chip key={d} active={filters.difficulty === d} onClick={() => set('difficulty', d)}>{d}</Chip>)}
          <span className="w-px h-5 bg-border mx-1" aria-hidden="true" />
          {LOCATIONS.map(l => <Chip key={l} active={filters.location === l} onClick={() => set('location', l)}>{l}</Chip>)}
        </FilterGroup>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-muted-foreground" aria-live="polite" data-test="catalog-count">
          {loadingFull ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Loading the full catalog…</span>
            : <><span className="font-semibold text-foreground">{filtered.length.toLocaleString()}</span> quest{filtered.length === 1 ? '' : 's'}{active ? ' match' : ''}</>}
        </p>
        <div className="flex items-center gap-2">
          {active && (
            <Button variant="ghost" size="sm" onClick={clear}><X className="w-4 h-4" aria-hidden="true" /> Clear filters</Button>
          )}
          <Button variant="outline" size="sm" onClick={surprise}><Dices className="w-4 h-4" aria-hidden="true" /> Surprise me</Button>
        </div>
      </div>

      {filtered.length === 0 && !loadingFull ? (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-muted-foreground mb-2">No quests match that combination</h2>
          <p className="text-muted-foreground mb-6">Loosen a filter, or let the generator pick for you.</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={clear}>Clear filters</Button>
            <Button asChild><Link href="/generator">Open the generator</Link></Button>
          </div>
        </div>
      ) : (
        <>
          <CatalogGrid quests={filtered.slice(0, visible)} />
          {filtered.length > visible && (
            <div className="flex justify-center mt-8">
              <Button variant="outline" size="lg" onClick={() => setVisible(v => v + PAGE)}>Load more adventures</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function finderParamsObject(f: FinderFilters): Record<string, string> {
  return Object.fromEntries(finderParams(f).entries());
}

export function QuestsClient(props: { initial: QuestCardData[]; total: number }) {
  return (
    <Suspense fallback={<CatalogStatic {...props} />}>
      <QuestsClientInner {...props} />
    </Suspense>
  );
}
